import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Chip,
  Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import RoleService from '../../services/roleService';
import api from '../../services/api';
import { getRoleName } from '../../utils/roleTranslations';
import { getPermissionName, groupPermissionsByCategory, orderedPermissionCategoryEntries } from '../../utils/permissionTranslations';
import PermissionInfoIcon from '../common/PermissionInfoIcon';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const TeamMemberRolePermissions = ({ formData, updateFormData, errors, storeId, i18n }) => {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [permissionsTranslations, setPermissionsTranslations] = useState({});
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const cancelledRef = useRef(false);

  // Load roles and permissions
  useEffect(() => {
    if (!storeId) return;

    cancelledRef.current = false;
    setLoading(true);

    const loadData = async () => {
      try {
        // Load roles
        const rolesKey = `getRoles-${storeId}`;
        const rolesData = await requestDeduplicator.deduplicate(rolesKey, async () => {
          return await RoleService.getRolesByStore();
        });

        if (!cancelledRef.current) {
          setRoles(rolesData || []);
        }

        // Load all permissions with translations
        const permissionsKey = 'getAllPermissions';
        const permissionsData = await requestDeduplicator.deduplicate(permissionsKey, async () => {
          const response = await api.get('/permissions');
          return response.data;
        });

        if (!cancelledRef.current) {
          setPermissionsTranslations(permissionsData || {});
          setAllPermissions(Object.keys(permissionsData || {}));
        }
      } catch (error) {
        if (!cancelledRef.current) {
          console.error('Failed to load roles or permissions:', error);
        }
      } finally {
        if (!cancelledRef.current) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelledRef.current = true;
    };
  }, [storeId]);

  // Update selected role when roleId changes
  useEffect(() => {
    if (formData.roleId) {
      const role = roles.find(r => r.id === formData.roleId);
      setSelectedRole(role || null);
    } else {
      setSelectedRole(null);
    }
  }, [formData.roleId, roles]);

  const handleRoleSelect = (role) => {
    if (formData.roleId === role.id) {
      // Deselect role
      updateFormData({
        roleId: null,
        additionalPermissions: [] // Clear additional permissions when role is deselected
      });
      setSelectedRole(null);
    } else {
      // Select role
      updateFormData({ roleId: role.id });
      setSelectedRole(role);
    }
  };

  const handlePermissionToggle = (permission) => {
    const currentAdditional = formData.additionalPermissions || [];
    const isSelected = currentAdditional.includes(permission);

    let newAdditional;
    if (isSelected) {
      newAdditional = currentAdditional.filter(p => p !== permission);
    } else {
      newAdditional = [...currentAdditional, permission];
    }

    updateFormData({ additionalPermissions: newAdditional });
  };

  const getRolePermissions = () => {
    if (!selectedRole) return [];

    if (Array.isArray(selectedRole.permissions)) {
      return selectedRole.permissions;
    }

    if (typeof selectedRole.permissions === 'object' && selectedRole.permissions !== null) {
      return Object.keys(selectedRole.permissions);
    }

    return [];
  };

  const rolePermissions = getRolePermissions();
  const additionalPermissions = formData.additionalPermissions || [];

  const groupedPermissions = groupPermissionsByCategory(allPermissions);

  const getPermissionLabel = (permission) => {
    if (permissionsTranslations[permission]) {
      return getPermissionName(permissionsTranslations[permission], i18n.language);
    }
    return permission;
  };

  const isPermissionInRole = (permission) => {
    return rolePermissions.includes(permission);
  };

  const isPermissionSelected = (permission) => {
    return additionalPermissions.includes(permission);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, color: BRAND.text, fontWeight: 600 }}>
        {t('teamMembers.registration.steps.step2.title')}
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
        {t('teamMembers.registration.steps.step2.description')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Role Selection */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {t('teamMembers.registration.role')} ({t('common.optional')})
          </Typography>
          <Grid container spacing={2}>
            {roles.map((role) => {
              const isSelected = formData.roleId === role.id;
              return (
                <Grid item xs={12} sm={6} md={4} key={role.id} sx={{ mt: 1 }}>
                  <Card
                    onClick={() => handleRoleSelect(role)}
                    sx={{
                      cursor: 'pointer',
                      border: isSelected ? `2px solid ${BRAND.accent}` : `2px solid ${BRAND.line}`,
                      backgroundColor: isSelected ? brandAlpha('accent', 0.08) : BRAND.surface,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: BRAND.accent,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {getRoleName(role.name, i18n.language)}
                        </Typography>
                        {isSelected && (
                          <Chip
                            label={t('common.selected')}
                            size="small"
                            sx={{
                              backgroundColor: BRAND.accent,
                              color: BRAND.onAccent,
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                      {isSelected && rolePermissions.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {t('teamMembers.registration.includesPermissions', { count: rolePermissions.length })}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Permissions Selection */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {t('teamMembers.registration.permissions')}
          </Typography>

          {selectedRole && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('teamMembers.registration.rolePermissionsInfo')}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              maxHeight: '500px',
              overflowY: 'auto',
              pr: 1
            }}
          >
            {orderedPermissionCategoryEntries(groupedPermissions).map(([category, perms]) => (
              <FormControl key={category} component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                  {t(`roles.modal.form.categories.${category}`)}
                </FormLabel>
                <FormGroup>
                  {perms.map(perm => {
                    const inRole = isPermissionInRole(perm);
                    const isSelected = isPermissionSelected(perm);
                    const isDisabled = inRole;

                    return (
                      <FormControlLabel
                        key={perm}
                        control={
                          <Checkbox
                            checked={inRole || isSelected}
                            onChange={() => handlePermissionToggle(perm)}
                            disabled={isDisabled}
                            sx={{
                              '&.Mui-disabled': {
                                color: BRAND.accentText,
                                '& + .MuiFormControlLabel-label': {
                                  color: '#666'
                                }
                              }
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">
                              {getPermissionLabel(perm)}
                            </Typography>
                            <PermissionInfoIcon
                              permissionTranslations={permissionsTranslations[perm]}
                              language={i18n.language}
                            />
                            {inRole && (
                              <Chip
                                label={t('teamMembers.registration.fromRole')}
                                size="small"
                                sx={{
                                  height: '20px',
                                  fontSize: '0.7rem',
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2'
                                }}
                              />
                            )}
                            {isSelected && !inRole && (
                              <Chip
                                label={t('teamMembers.registration.additional')}
                                size="small"
                                sx={{
                                  height: '20px',
                                  fontSize: '0.7rem',
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32'
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    );
                  })}
                </FormGroup>
              </FormControl>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TeamMemberRolePermissions;

